import { IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights, Sizes } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';
import { DetailsListSizes } from '../DetailsList/DetailsList.styles';

export interface IListCellStyles {
  root: IStyle;
  content?: IStyle;
  header?: IStyle;
  text?: IStyle;
  depth?: IStyle;
  depthLine?: IStyle;
  depthEnd?: IStyle;
  depthHasNextSibling?: IStyle;
  depthEmpty?: IStyle;
  icon?: IStyle;
}

export interface IListCellClassNames {
  root: string;
  content: string;
  header: string;
  text: string;
  depth: string;
  depthLine: string;
  depthEnd: string;
  depthHasNextSibling: string;
  depthEmpty: string;
  icon: string;
}

export const listCellStylesDefault = (theme: IH2OTheme): Partial<IListCellStyles> => ({
  root: {
    display: 'flex',
    position: 'relative',
    paddingLeft: DetailsListSizes.CellLeftPadding,
    minWidth: '0px',
    '&.is-selected': {
      '.h2o-ListCell-text': {
        color: theme.semanticColors?.textPrimary,
      },
    },
    '&.is-last': {
      paddingRight: DetailsListSizes.CellLeftPadding,
    },
    '&.is-disabled': {
      pointerEvents: 'none',
      backgroundColor: theme.semanticColors?.buttonDisabledBackground,
      color: theme.semanticColors?.textDisabled,
      '.h2o-ListCell-text, .h2o-ListCell-header': {
        color: theme.semanticColors?.textDisabled,
      },
    },
    '&.is-changed': {
      backgroundColor: theme.palette?.primary100,
      '::after': {
        display: 'block',
        position: 'absolute',
        top: '2px',
        right: '2px',
        content: "'*'",
      },
    },
    '&.has-error': {
      backgroundColor: theme.semanticColors?.messageBarBackgroundError,
      '::after': {
        display: 'block',
        position: 'absolute',
        top: '2px',
        right: '2px',
        content: "'*'",
        color: theme.semanticColors?.inputErrorMessageText,
      },
    },
    '&.has-icon.has-depth .h2o-ListCell-depth': {
      marginLeft: '8px',
    },
  },
  content: { overflow: 'hidden', ...CommonStyles.flexCenterVertically },
  header: { fontWeight: FontWeights.semiBold, ...CommonStyles.truncateString, paddingBottom: 6 },
  text: {
    color: theme.semanticColors?.textSecondary,
    ...CommonStyles.truncateString,
  },
  depth: {
    display: 'flex',
    marginRight: '8px',
    '& > div': {
      width: '24px',
      height: '100%',
      position: 'relative',
    },
  },
  depthLine: {
    'div:first-child': {
      position: 'absolute',
      top: '0px',
      left: '12px',
      height: '100%',
      borderLeft: `1px solid ${theme.palette?.gray400}`,
    },
  },
  depthEnd: {
    'div:first-child': {
      position: 'absolute',
      top: '0px',
      left: '12px',
      height: '50%',
      width: '12px',
      borderLeft: `1px solid ${theme.palette?.gray400}`,
      borderBottom: `1px solid ${theme.palette?.gray400}`,
    },
  },
  depthHasNextSibling: {
    'div:first-child': {
      position: 'absolute',
      top: '0px',
      left: '12px',
      height: '100%',
      borderLeft: `1px solid ${theme.palette?.gray400}`,
    },
    'div:last-child': {
      position: 'absolute',
      top: '0px',
      left: '12px',
      height: '50%',
      width: '12px',
      borderBottom: `1px solid ${theme.palette?.gray400}`,
    },
  },
  depthEmpty: {},
  icon: {
    height: '100%',
    marginRight: Sizes.padding,
    ...CommonStyles.flexCenterVertically,
    'div:first-child': {
      width: '40px',
      height: '40px',
      borderRadius: Sizes.borderRadius,
      backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
      textAlign: 'center',
      ...CommonStyles.flexCenterVertically,
      i: {
        // Pair with the tile's own background token rather than hardcoding white: on the brand
        // yellow primary that gave 1.54:1, under the 3:1 WCAG 1.4.11 floor for graphical objects.
        color: theme.semanticColors?.buttonPrimaryText,
        fontSize: FontSizes.buttonIcon,
      },
    },
  },
});

export const listCellStylesNoLeftRightPadding: Partial<IListCellStyles> = {
  root: {
    paddingLeft: '0px',
    '&.is-last': {
      paddingRight: '0px',
    },
  },
};
